import { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { FaEdit, FaRegTrashAlt } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import LayoutNotFilter from '../../../data/LayoutNotFilter';
import LayoutTable from '../../../data/LayoutTable';
import ActiveInactiv from '../../../data/activeInactive';
import CustomLoader from '../../../data/customLoader';
import ModalBootstrap from '../../../data/Modal';
import { paginationOptions } from '../../../data/PagineteInfo';
import tableStyleDefault from '../../../../Services/tableStyleDefault';
import Format from '../../../../Services/Format';
import DateUtils from '../../../../Services/DateUtils';
import { notify, notifyError } from '../../../notify/notify-component';
import { AppDispatch, useAppSelector } from '../../../../redux/store';
import { ICategorieFinancial } from '../../../../redux/features/categorieFinancial/interface';
import {
  deleteCategoryFinancialAsync,
  getCategorieFinancialAsync,
} from '../../../../redux/features/categorieFinancial/services';
import CategoriaModal from './CategoriaModal';
import { ENTRY_TYPES, EntryType } from './entryTypes';
import * as S from './styles';

interface Props {
  Title: string;
}

/** Categorias financeiras de receita e de despesa, uma aba para cada tipo. */
export default function ListaCategoriaMovimentacao({ Title }: Props) {
  const storeId = useAppSelector(({ storeMeReducer }) => storeMeReducer.value)?.storeActive?.id;
  const { revenue, expenxe, loading } = useAppSelector(
    ({ categorieFinancialReducer }) => categorieFinancialReducer.value
  );
  const dispatch = useDispatch<AppDispatch>();

  const [type, setType] = useState<EntryType>('revenue');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ category?: ICategorieFinancial } | null>(null);
  const [categoryDelete, setCategoryDelete] = useState<ICategorieFinancial | null>(null);

  const reload = () => {
    if (storeId) dispatch(getCategorieFinancialAsync(storeId));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const categories: Record<EntryType, ICategorieFinancial[]> = { revenue, expense: expenxe };

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = type === 'revenue' ? revenue : expenxe;
    return term ? list.filter((item) => item.description?.toLowerCase().includes(term)) : list;
  }, [type, search, revenue, expenxe]);

  // A API recusa excluir categoria usada em lançamentos (chave estrangeira).
  const handleDelete = async () => {
    const category = categoryDelete;
    setCategoryDelete(null);
    if (!category) return;

    const result = await dispatch(deleteCategoryFinancialAsync(category.uuid));
    if (deleteCategoryFinancialAsync.fulfilled.match(result)) {
      notify('Categoria excluída.');
      reload();
      return;
    }
    notifyError(
      'Não foi possível excluir. Se a categoria já foi usada em lançamentos, desative-a em vez de excluir.'
    );
  };

  const columns: any = [
    {
      name: 'Descrição',
      grow: 3,
      sortable: true,
      selector: (row: ICategorieFinancial) => row.description,
      cell: (row: ICategorieFinancial) => (
        <S.Description>
          <strong>{row.description}</strong>
          {row.note && <small>{row.note}</small>}
        </S.Description>
      ),
    },
    {
      name: 'Código',
      center: true,
      grow: 0.5,
      sortable: true,
      selector: (row: ICategorieFinancial) => row.id,
    },
    {
      name: 'Cadastro',
      center: true,
      grow: 0.8,
      sortable: true,
      selector: (row: ICategorieFinancial) => row.created_at,
      format: (row: ICategorieFinancial) =>
        Format.date(DateUtils.toDate(row.created_at), 'DD/MM/YYYY'),
    },
    {
      name: 'Status',
      center: true,
      grow: 0.6,
      cell: (row: ICategorieFinancial) => (
        <ActiveInactiv status={row.is_active} text={row.is_active ? 'Ativo' : 'Inativo'} />
      ),
    },
    {
      name: 'Ação',
      center: true,
      grow: 0.5,
      cell: (row: ICategorieFinancial) => (
        <S.Actions>
          <button type="button" title="Editar" onClick={() => setEditing({ category: row })}>
            <FaEdit size={15} color="#8e8d8d" />
          </button>
          <button type="button" title="Excluir" onClick={() => setCategoryDelete(row)}>
            <FaRegTrashAlt size={14} color="#dc2229" />
          </button>
        </S.Actions>
      ),
    },
  ];

  const typeLabel = ENTRY_TYPES[type].label.toLowerCase();

  return (
    <>
      <CategoriaModal
        show={editing !== null}
        type={type}
        category={editing?.category}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />
      <ModalBootstrap
        state={categoryDelete !== null}
        setState={(open: boolean) => !open && setCategoryDelete(null)}
        text={`Tem certeza de que deseja excluir a categoria "${categoryDelete?.description}"?`}
        handleAction={handleDelete}
      />

      <LayoutNotFilter title={Title} onClickAdd={() => setEditing({})}>
        <S.Toolbar>
          <S.Segmented role="tablist" aria-label="Tipo de categoria">
            {(Object.keys(ENTRY_TYPES) as EntryType[]).map((key) => (
              <S.SegmentButton
                key={key}
                type="button"
                role="tab"
                aria-selected={type === key}
                $active={type === key}
                $tone={key}
                onClick={() => setType(key)}
              >
                {ENTRY_TYPES[key].plural}
                <span className="count">{categories[key].length}</span>
              </S.SegmentButton>
            ))}
          </S.Segmented>

          <S.Search>
            <FiSearch size={14} />
            <input
              type="search"
              placeholder="Buscar categoria"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </S.Search>
        </S.Toolbar>

        <LayoutTable>
          <DataTable
            columns={columns}
            data={rows}
            striped
            highlightOnHover
            pagination
            paginationPerPage={15}
            paginationComponentOptions={paginationOptions}
            customStyles={tableStyleDefault}
            progressPending={loading}
            progressComponent={<CustomLoader />}
            noDataComponent={
              <S.Empty>
                {search.trim()
                  ? `Nenhuma categoria de ${typeLabel} encontrada para "${search.trim()}".`
                  : `Nenhuma categoria de ${typeLabel} cadastrada. Use "Adicionar" para criar a primeira.`}
              </S.Empty>
            }
          />
        </LayoutTable>
      </LayoutNotFilter>
    </>
  );
}
